import type { Kpi } from '../lib/types';

/**
 * Placeholder rendering of the well progress chart page (depth vs. days),
 * plus the KPI gauges that sit under it.
 */

const PLANNED: [number, number][] = [
  [0, 0],
  [1.5, 180],
  [3, 520],
  [5, 900],
  [8, 1250],
  [12, 1600],
  [17, 1980],
  [23, 2320],
  [30, 2640],
  [38, 2980],
  [46, 3135],
];

const ACTUAL: [number, number][] = [
  [0, 0],
  [1.2, 210],
  [2.8, 560],
  [4.6, 940],
  [7.4, 1210],
  [11.5, 1540],
  [16, 1870],
  [21, 2180],
  [27, 2450],
  [33, 2673],
];

const SECTIONS = [
  { label: '26" Conductor', top: 0, bottom: 180 },
  { label: '17 1/2" Surface', top: 180, bottom: 900 },
  { label: '12 1/4" Intermediate', top: 900, bottom: 1980 },
  { label: '8 1/2" Production', top: 1980, bottom: 2980 },
];

const W = 620;
const H = 360;
const PAD = { top: 22, right: 18, bottom: 34, left: 52 };
const MAX_DAYS = 48;
const MAX_DEPTH = 3200;

const x = (days: number) => PAD.left + (days / MAX_DAYS) * (W - PAD.left - PAD.right);
const y = (depth: number) => PAD.top + (depth / MAX_DEPTH) * (H - PAD.top - PAD.bottom);

const line = (pts: [number, number][]) =>
  pts.map(([d, z], i) => `${i === 0 ? 'M' : 'L'}${x(d).toFixed(1)},${y(z).toFixed(1)}`).join(' ');

export function ProgressChart({ title, kpis }: { title: string; kpis: Kpi[] }) {
  return (
    <article className="paper" aria-label={title}>
      <h3 className="chart-title">{title.toUpperCase()}</h3>

      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Depth versus days: planned against actual progress"
      >
        <rect x={PAD.left} y={PAD.top} width={W - PAD.left - PAD.right} height={H - PAD.top - PAD.bottom} fill="#fff" stroke="#c9d2da" />

        {SECTIONS.map((s) => (
          <g key={s.label}>
            <rect
              x={PAD.left + 1}
              y={y(s.top)}
              width={W - PAD.left - PAD.right - 2}
              height={y(s.bottom) - y(s.top)}
              fill="#0f766e"
              opacity="0.05"
            />
            <text x={PAD.left + 6} y={y(s.top) + 12} className="chart-section">
              {s.label}
            </text>
          </g>
        ))}

        {Array.from({ length: 9 }, (_, i) => i * 400).map((depth) => (
          <g key={depth}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(depth)}
              y2={y(depth)}
              stroke="#e3e9ee"
            />
            <text x={PAD.left - 8} y={y(depth) + 3.5} className="chart-axis chart-axis--y">
              {depth}
            </text>
          </g>
        ))}

        {Array.from({ length: 7 }, (_, i) => i * 8).map((days) => (
          <g key={days}>
            <line y1={PAD.top} y2={H - PAD.bottom} x1={x(days)} x2={x(days)} stroke="#e3e9ee" />
            <text x={x(days)} y={H - PAD.bottom + 14} className="chart-axis">
              {days}
            </text>
          </g>
        ))}

        <text x={16} y={H / 2} className="chart-label" transform={`rotate(-90 16 ${H / 2})`}>
          Depth (mMD)
        </text>
        <text x={W / 2} y={H - 6} className="chart-label">
          Days from spud
        </text>

        <path d={line(PLANNED)} fill="none" stroke="#1f6fb2" strokeWidth="1.8" strokeDasharray="5 3" />
        <path d={line(ACTUAL)} fill="none" stroke="#0f766e" strokeWidth="2.2" />

        {ACTUAL.map(([d, z]) => (
          <circle key={`${d}-${z}`} cx={x(d)} cy={y(z)} r="2.4" fill="#0f766e" />
        ))}

        <g className="chart-legend" transform={`translate(${W - PAD.right - 150} ${PAD.top + 10})`}>
          <line x1="0" x2="22" y1="0" y2="0" stroke="#1f6fb2" strokeWidth="1.8" strokeDasharray="5 3" />
          <text x="28" y="3.5">
            Planned
          </text>
          <line x1="0" x2="22" y1="16" y2="16" stroke="#0f766e" strokeWidth="2.2" />
          <text x="28" y="19.5">
            Actual
          </text>
        </g>
      </svg>

      <div className="gauges">
        {kpis.map((kpi, i) => (
          <Gauge key={`${kpi.label}-${i}`} kpi={kpi} />
        ))}
      </div>
    </article>
  );
}

function Gauge({ kpi }: { kpi: Kpi }) {
  const pct = Math.max(0, Math.min(1, kpi.value / kpi.max));
  const r = 34;
  const circumference = Math.PI * r; // half circle
  const dash = circumference * pct;

  const tone = pct < 0.5 ? '#2e9e4f' : pct < 0.85 ? '#e0a416' : '#d03b34';

  return (
    <figure className="gauge">
      <svg viewBox="0 0 90 58" role="img" aria-label={`${kpi.label}: ${kpi.value}${kpi.unit}`}>
        <path
          d={`M ${45 - r} 46 A ${r} ${r} 0 0 1 ${45 + r} 46`}
          fill="none"
          stroke="#e3e9ee"
          strokeWidth="9"
          strokeLinecap="round"
        />
        <path
          d={`M ${45 - r} 46 A ${r} ${r} 0 0 1 ${45 + r} 46`}
          fill="none"
          stroke={tone}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
        />
        <text x="45" y="44" className="gauge-value">
          {kpi.value}
        </text>
      </svg>
      <figcaption>{kpi.label}</figcaption>
    </figure>
  );
}
